import React from 'react';
import {useNavigate} from 'react-router-dom';
import {ConfirmationModal, ConfirmationSuccessIcon} from '@rocket-ui/components';
import {useCreateReceipt} from '@rocket-ui/lk-api';
import {
	GenerateReceiptForm,
	ProfileAlertsBanner,
	RECEIPT_SUCCESS_DESCRIPTION,
	RECEIPT_SUCCESS_TITLE,
	buildReceiptRequest,
	canSubmitReceipt,
	formatReceiptTotal,
	sumReceiptServicesCents,
	type ReceiptClientType,
	type ReceiptServiceItem,
} from 'features/profile';
import {RegistrationButton} from 'features/registration';
import {ROUTES} from 'app/router/routes';
import styles from './GenerateReceiptPage.module.css';

const backIcon = (
	<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
		<path d="M10 6L4 12L10 18" stroke="currentColor" strokeWidth="1.6" />
		<path
			fillRule="evenodd"
			clipRule="evenodd"
			d="M20 12.9135L5 12.9135L5 11.1992L20 11.1992L20 12.9135Z"
			fill="currentColor"
		/>
	</svg>
);

export function GenerateReceiptPage(): React.ReactElement {
	const navigate = useNavigate();
	const createReceiptMutation = useCreateReceipt();
	const [date, setDate] = React.useState('');
	const [services, setServices] = React.useState<ReceiptServiceItem[]>([]);
	const [clientType, setClientType] = React.useState<ReceiptClientType>('individual');
	const [isForeignOrganization, setIsForeignOrganization] = React.useState(false);
	const [inn, setInn] = React.useState('');
	const [organizationName, setOrganizationName] = React.useState('');
	const [isSuccessOpen, setIsSuccessOpen] = React.useState(false);
	const [submitError, setSubmitError] = React.useState<string | null>(null);

	const formValues = {
		date,
		services,
		clientType,
		isForeignOrganization,
		inn,
		organizationName,
	};
	const totalCents = sumReceiptServicesCents(services);
	const isFormComplete = canSubmitReceipt(formValues);
	const showSubmitButton = isFormComplete || createReceiptMutation.isLoading;
	const showTotal = totalCents > 0;
	const showFooter = showTotal || showSubmitButton;

	const handleSuccessContinue = () => {
		setIsSuccessOpen(false);
		navigate(ROUTES.profile);
	};

	const handleSubmit = () => {
		const request = buildReceiptRequest(formValues);

		if (!request || createReceiptMutation.isLoading) {
			return;
		}

		setSubmitError(null);
		createReceiptMutation.mutate(request, {
			onSuccess: () => {
				setIsSuccessOpen(true);
			},
			onError: () => {
				setSubmitError('Не получилось сформировать чек. Попробуйте ещё раз');
			},
		});
	};

	return (
		<div className={styles.root}>
			<header className={styles.header}>
				<button
					type="button"
					className={styles.backButton}
					onClick={() => navigate(ROUTES.profile)}
					aria-label="Назад"
				>
					{backIcon}
				</button>
				<h1 className={styles.title}>Сформировать чек</h1>
			</header>

			<div className={styles.content}>
				<div className={styles.contentInner}>
					<ProfileAlertsBanner />
					<GenerateReceiptForm
						date={date}
						onDateChange={setDate}
						services={services}
						onServicesChange={setServices}
						clientType={clientType}
						onClientTypeChange={setClientType}
						isForeignOrganization={isForeignOrganization}
						onForeignOrganizationChange={setIsForeignOrganization}
						inn={inn}
						onInnChange={setInn}
						organizationName={organizationName}
						onOrganizationNameChange={setOrganizationName}
						onFormChange={() => setSubmitError(null)}
					/>

					{submitError ? <p className={styles.errorText}>{submitError}</p> : null}
				</div>
			</div>

			{showFooter ? (
				<footer className={styles.footer}>
					<div className={styles.footerInner}>
						{showTotal ? <p className={styles.total}>Итого: {formatReceiptTotal(totalCents)}</p> : null}
						{showSubmitButton ? (
							<RegistrationButton
								variant="primary"
								className={styles.submitButton}
								onClick={handleSubmit}
								disabled={!isFormComplete}
								loading={createReceiptMutation.isLoading}
							>
								Выдать чек
							</RegistrationButton>
						) : null}
					</div>
				</footer>
			) : null}

			<ConfirmationModal
				isOpen={isSuccessOpen}
				title={RECEIPT_SUCCESS_TITLE}
				description={RECEIPT_SUCCESS_DESCRIPTION}
				confirmText="Понятно"
				illustration={ConfirmationSuccessIcon}
				onConfirm={handleSuccessContinue}
				onCancel={handleSuccessContinue}
			/>
		</div>
	);
}
